import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { Anotacao } from './AnnotationPin';
import { AnnotationThread } from './AnnotationThread';

interface AnnotationPanelProps {
  anotacoes: Anotacao[];
  selectedId: string | null;
  currentPage: number;
  onSelect: (id: string | null) => void;
  onReply: (anotacaoId: string, texto: string) => Promise<void>;
  onResolve: (anotacaoId: string) => Promise<void>;
}

export function AnnotationPanel({ anotacoes, selectedId, currentPage, onSelect, onReply, onResolve }: AnnotationPanelProps) {
  const [filtro, setFiltro] = useState<'todas' | 'pagina' | 'pendentes'>('pagina');

  const filtered = anotacoes.filter(a => {
    if (filtro === 'pagina') return a.pagina === currentPage;
    if (filtro === 'pendentes') return !a.resolvido;
    return true;
  });

  const pendentes = anotacoes.filter(a => !a.resolvido).length;
  const resolvidas = anotacoes.filter(a => a.resolvido).length;

  return (
    <div className="w-80 xl:w-96 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Anotacoes
          </h3>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-yellow-600 dark:text-yellow-400">{pendentes} pendentes</span>
            <span className="text-green-600 dark:text-green-400">{resolvidas} resolvidas</span>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex gap-1">
          {[
            { key: 'pagina' as const, label: `Pag. ${currentPage}` },
            { key: 'pendentes' as const, label: 'Pendentes' },
            { key: 'todas' as const, label: 'Todas' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFiltro(f.key)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                filtro === f.key
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de anotacoes */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Nenhuma anotacao {filtro === 'pagina' ? 'nesta pagina' : filtro === 'pendentes' ? 'pendente' : ''}</p>
            <p className="text-xs mt-1">Clique no PDF para adicionar</p>
          </div>
        ) : (
          filtered.map(a => (
            <div
              key={a.id}
              id={`anotacao-${a.id}`}
              className={`transition-all ${selectedId === a.id ? 'ring-2 ring-blue-500 rounded-lg' : ''}`}
              onClick={() => onSelect(a.id)}
            >
              <AnnotationThread
                anotacao={a}
                onReply={onReply}
                onResolve={onResolve}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
